import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced } from '../../lib/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { money, num, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface RepRow {
  employeeId: string;
  name: string;
  code: string | null;
  jobTitle: string | null;
  userId: string | null;
  target: number | null;
  achieved: number;
  achievementPct: number | null;
  discountBudget: number | null;
  discountUsed: number;
  discountRemaining: number | null;
}

interface Overview {
  period: string;
  periodLabel: string;
  enforcement: 'OFF' | 'WARN' | 'BLOCK';
  defaultBudget: number | null;
  rows: RepRow[];
  totals: { target: number; achieved: number; discountUsed: number; reps: number };
}

const ENFORCEMENT_LABEL: Record<Overview['enforcement'], { label: string; kind: string }> = {
  OFF: { label: 'رصيد الخصومات: غير مُفعَّل', kind: 'neutral' },
  WARN: { label: 'رصيد الخصومات: تنبيه فقط', kind: 'warning' },
  BLOCK: { label: 'رصيد الخصومات: مُلزِم', kind: 'success' },
};

/** أقصى شهرٍ يُتاح اختياره — الشهر الجاري. */
function thisMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * شريط النسبة — يعيد استعمال أنماط تحقيق العمولات، واللون يُمرَّر صراحةً كي
 * يخدم معنيين متعاكسين: بلوغُ الهدف خيرٌ، واستهلاكُ الرصيد اقترابٌ من حدّه.
 */
function Bar({ pct, color, label }: { pct: number | null; color: string; label: string }) {
  if (pct == null) return <span className="cell-mute">—</span>;
  const width = Math.min(100, Math.max(0, pct) / 1.5);
  return (
    <div className="achieve" title={label}>
      <div className="achieve-bar">
        <span className="achieve-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      <span className="achieve-num num">{num(Math.round(pct))}%</span>
    </div>
  );
}

/** لون شريط تحقيق الهدف: أخضر عند البلوغ، فأصفر، فأحمر. */
function targetColor(pct: number | null): string {
  if (pct == null) return '#9BA3AE';
  if (pct >= 100) return '#51A521';
  if (pct >= 70) return '#BF930B';
  return '#DC2626';
}

/** لون شريط استهلاك الرصيد: أخضر ما دام بعيدًا، فأصفر، فأحمر عند النفاد. */
function usageColor(pct: number): string {
  if (pct >= 100) return '#DC2626';
  if (pct >= 80) return '#BF930B';
  return '#51A521';
}

export function Targets() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('MANAGER');
  const [period, setPeriod] = useState(thisMonth());
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<RepRow | null>(null);
  const [coaching, setCoaching] = useState<RepRow | null>(null);
  const debounced = useDebounced(search);

  const data = useQuery({
    queryKey: ['targets', period],
    queryFn: () => api<Overview>(`/sales/targets${qs({ period })}`),
  });

  const rows = useMemo(() => {
    const all = data.data?.rows ?? [];
    const q = debounced.trim();
    if (!q) return all;
    return all.filter((r) => r.name.includes(q) || (r.code ?? '').includes(q));
  }, [data.data, debounced]);

  const enforcement = data.data?.enforcement ?? 'OFF';

  return (
    <div className="page">
      <PageHead
        title="الأهداف والخصومات"
        subtitle={data.data ? `${data.data.periodLabel} · ${plural(data.data.totals.reps, WORDS.rep)}` : undefined}
        actions={
          <Link to="/settings/commissions" className="btn">
            <Icon name="settings" size={15} />
            سياسة الخصومات
          </Link>
        }
      />

      {data.isError && <ErrorState error={data.error} onRetry={() => void data.refetch()} />}

      {data.data && <RecordStats items={cards(data.data)} storageKey="goyone.stats.targets" defaults={['target', 'achieved', 'discount', 'reps']} />}

      <div className="toolbar sticky-bar">
        <div className="field" style={{ margin: 0 }}>
          <input
            className="input num"
            type="month"
            max={thisMonth()}
            value={period}
            onChange={(e) => setPeriod(e.target.value || thisMonth())}
            aria-label="الشهر"
          />
        </div>
        <SearchBox value={search} onChange={setSearch} placeholder="بحث باسم المندوب أو رقمه" />
        <Badge kind={ENFORCEMENT_LABEL[enforcement].kind}>{ENFORCEMENT_LABEL[enforcement].label}</Badge>
      </div>

      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {canEdit && <th className="col-act" />}
                <th>المندوب</th>
                <th className="col-num">الهدف</th>
                <th className="col-num">المُحقَّق</th>
                <th>التحقيق</th>
                <th className="col-num">رصيد الخصومات</th>
                <th className="col-num">المستهلك</th>
                <th>المتبقّي</th>
              </tr>
            </thead>

            {data.isPending && <TableSkeleton cols={canEdit ? 8 : 7} />}

            {data.data && (
              <tbody>
                {rows.map((r) => {
                  const usagePct =
                    r.discountBudget && r.discountBudget > 0
                      ? (r.discountUsed / r.discountBudget) * 100
                      : null;
                  return (
                    <tr key={r.employeeId}>
                      {canEdit && (
                        <td className="col-act">
                          <span className="row" style={{ gap: 2 }}>
                            <button
                              className="btn btn-sm btn-icon btn-ghost"
                              title={`طريق ${r.name} إلى هدفه`}
                              aria-label={`طريق ${r.name} إلى هدفه`}
                              onClick={() => setCoaching(r)}
                            >
                              <Icon name="zap" size={15} />
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-ghost"
                              title={`تعديل هدف ورصيد ${r.name}`}
                              aria-label={`تعديل هدف ورصيد ${r.name}`}
                              onClick={() => setEditing(r)}
                            >
                              <Icon name="edit" size={15} />
                            </button>
                          </span>
                        </td>
                      )}
                      <td>
                        <Link to={`/hr/employees/${r.employeeId}`} className="cell-strong link">
                          {r.name}
                        </Link>
                        {r.code && <div className="tiny mute">{r.code}</div>}
                        {!r.userId && <div className="tiny mute">بلا حساب مستخدم — لا يُنسب إليه بيع</div>}
                      </td>
                      <td className="num cell-mute">{r.target == null ? '—' : money(r.target)}</td>
                      <td className="num cell-strong">{money(r.achieved)}</td>
                      <td style={{ minWidth: 120 }}>
                        <Bar
                          pct={r.achievementPct}
                          color={targetColor(r.achievementPct)}
                          label={r.achievementPct == null ? 'لا هدف محدَّد' : `${num(r.achievementPct)}% من الهدف`}
                        />
                      </td>
                      <td className="num cell-mute">
                        {r.discountBudget == null ? 'بلا رصيد' : money(r.discountBudget)}
                      </td>
                      <td className="num">{money(r.discountUsed)}</td>
                      <td style={{ minWidth: 120 }}>
                        {r.discountRemaining == null ? (
                          <span className="cell-mute">—</span>
                        ) : usagePct == null ? (
                          <span className="cell-mute">{money(r.discountRemaining)}</span>
                        ) : (
                          <Bar
                            pct={usagePct}
                            color={usageColor(usagePct)}
                            label={`مُستهلك ${num(Math.round(usagePct))}% — متبقٍّ ${money(r.discountRemaining)}`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {data.data && rows.length === 0 && (
          <EmptyState
            title="لا مندوبو مبيعات"
            text="حدِّد الموظفين المعنيين بأنهم مندوبو مبيعات في ملفاتهم ليظهروا هنا بأهدافهم وأرصدتهم."
          />
        )}
      </div>

      {editing && (
        <TargetEdit
          rep={editing}
          defaultBudget={data.data?.defaultBudget ?? null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void data.refetch();
          }}
        />
      )}

      {coaching && (
        <TargetCoach
          employeeId={coaching.employeeId}
          name={coaching.name}
          period={period}
          onClose={() => setCoaching(null)}
        />
      )}
    </div>
  );
}

interface CoachPick {
  kind: 'quotation' | 'deal';
  id: string;
  label: string;
  account: string | null;
  value: number;
  expected: number;
  confidence: number;
  dueAt: string | null;
}

interface CoachData {
  periodLabel: string;
  employee: { id: string; name: string } | null;
  target: number | null;
  achieved: number;
  gap: number;
  pipelineExpected: number;
  covered: number;
  picks: CoachPick[];
  tips: string[];
}

/**
 * الطريق إلى الهدف — ماذا يُلاحَق اليوم.
 *
 * لا نصائح عامة: القائمة من خطّ مبيعات المندوب نفسه، مرتَّبةً بما يُتوقّع
 * تحصيله. والعرض المرسَل يُقدَّر بنصف قيمته والصفقة باحتمالية مرحلتها — وهو
 * تقديرٌ يُقال إنه تقدير، لا وعدٌ بأن المجموع سيصل.
 */
function TargetCoach({
  employeeId,
  name,
  period,
  onClose,
}: {
  employeeId: string;
  name: string;
  period: string;
  onClose: () => void;
}) {
  const coach = useQuery({
    queryKey: ['targets', 'coach', employeeId, period],
    queryFn: () => api<CoachData>(`/sales/targets/coach${qs({ employeeId, period })}`),
  });

  const d = coach.data;

  return (
    <Modal
      title={`طريق ${name} إلى الهدف`}
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      {coach.isPending && <div className="skeleton" style={{ height: 220 }} />}
      {coach.isError && <ErrorState error={coach.error} onRetry={() => void coach.refetch()} />}

      {d && (
        <>
          <div className="stats">
            <Stat
              label="الهدف"
              value={<span className="num">{d.target == null ? 'بلا هدف' : money(d.target)}</span>}
            />
            <Stat label="المُحقَّق" value={<span className="num">{money(d.achieved)}</span>} />
            <Stat
              label="المتبقّي"
              value={<span className="num">{d.gap === 0 ? 'تحقّق' : money(d.gap)}</span>}
            />
          </div>

          {d.tips.length > 0 && (
            <div className="col mt-4" style={{ gap: 'var(--sp-2)' }}>
              {d.tips.map((t) => (
                <div key={t} className="alert alert-info">
                  <Icon name="zap" size={15} />
                  <div>{t}</div>
                </div>
              ))}
            </div>
          )}

          {d.picks.length > 0 && (
            <div className="mt-4">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>ابدأ بهذه</strong>
                <span className="tiny mute">
                  متوقّعٌ منها {money(d.covered)}
                  {d.gap > 0 && ` من ${money(d.gap)}`}
                </span>
              </div>

              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>المستند</th>
                      <th className="col-num">قيمته</th>
                      <th className="col-num">المتوقّع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.picks.map((p) => (
                      <tr key={`${p.kind}-${p.id}`}>
                        <td>
                          <Link
                            to={
                              p.kind === 'quotation'
                                ? `/sales/quotations/${p.id}`
                                : `/sales/deals?q=${encodeURIComponent(p.label)}`
                            }
                            className="cell-strong link"
                          >
                            {p.label}
                          </Link>
                          <div className="tiny mute">
                            {p.account ?? '—'} · {p.kind === 'quotation' ? 'عرض مرسَل' : 'صفقة مفتوحة'} ·
                            احتمالية {num(p.confidence)}%
                          </div>
                        </td>
                        <td className="num cell-mute">{money(p.value)}</td>
                        <td className="num cell-strong">{money(p.expected)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* التقدير يُقال إنه تقدير: مجموعٌ لا يَعِد بأنه سيصل. */}
              <p className="tiny mute mt-2">
                «المتوقّع» تقديرٌ لا وعد: العرض المرسَل بنصف قيمته، والصفقة باحتمالية مرحلتها.
              </p>
            </div>
          )}

          {d.picks.length === 0 && d.target != null && d.gap > 0 && (
            <EmptyState
              title="لا مستندات مفتوحة"
              text="لا عروض مرسَلة ولا صفقات مفتوحة تُحسب في الطريق إلى الهدف. ابدأ بصفقةٍ جديدة."
            />
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * ضبط هدف المندوب ورصيد خصوماته.
 *
 * تركُ الحقل فارغًا يعيد القيمة إلى الافتراضي: الهدف يصير «بلا هدف»، والرصيد
 * يعود إلى رصيد الشركة — فلا يُحبَس المندوب على رقمٍ ضُبط ثم أُريد محوه.
 */
function TargetEdit({
  rep,
  defaultBudget,
  onClose,
  onSaved,
}: {
  rep: RepRow;
  defaultBudget: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(rep.target == null ? '' : String(rep.target));
  const [budget, setBudget] = useState(
    rep.discountBudget == null ? '' : String(rep.discountBudget),
  );

  const save = useMutation({
    mutationFn: () =>
      api(`/sales/targets/${rep.employeeId}`, {
        method: 'PATCH',
        body: {
          target: target.trim() === '' ? null : Number(target),
          discountBudget: budget.trim() === '' ? null : Number(budget),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
      onSaved();
    },
  });

  return (
    <Modal
      title={`هدف ورصيد ${rep.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="t-target">
            الهدف الشهري
          </label>
          <input
            id="t-target"
            className="input num"
            type="number"
            min="0"
            step="any"
            placeholder="بلا هدف"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <span className="tiny mute">اتركه فارغًا لإلغاء الهدف.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="t-budget">
            رصيد الخصومات الشهري
          </label>
          <input
            id="t-budget"
            className="input num"
            type="number"
            min="0"
            step="any"
            placeholder={defaultBudget == null ? 'بلا رصيد' : `الافتراضي ${money(defaultBudget)}`}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
          <span className="tiny mute">
            اتركه فارغًا ليأخذ الرصيد الافتراضي للشركة.
          </span>
        </div>
      </div>
    </Modal>
  );
}

function cards(o: Overview): RecordStat[] {
  const t = o.totals;
  const pct = t.target > 0 ? Math.round((t.achieved / t.target) * 100) : null;
  return [
    {
      key: 'target',
      label: 'إجمالي الهدف',
      group: 'الهدف',
      icon: 'chartBar',
      color: '#1D46A6',
      value: t.target > 0 ? money(t.target) : 'بلا أهداف',
      numeric: true,
    },
    {
      key: 'achieved',
      label: 'المُحقَّق (مفوتَر)',
      group: 'الهدف',
      icon: 'wallet',
      color: '#51A521',
      value: money(t.achieved),
      numeric: true,
      note: pct == null ? undefined : `${num(pct)}% من الهدف`,
    },
    {
      key: 'discount',
      label: 'خصومات ممنوحة',
      group: 'الخصومات',
      icon: 'receipt',
      color: '#BF930B',
      value: money(t.discountUsed),
      numeric: true,
      note: 'عروضٌ مرسَلة ومقبولة هذا الشهر',
    },
    {
      key: 'reps',
      label: 'مندوبو المبيعات',
      group: 'العدد',
      icon: 'users',
      color: '#3672EA',
      value: t.reps,
      numeric: true,
    },
    {
      key: 'gap',
      label: 'المتبقّي للهدف',
      group: 'الهدف',
      icon: 'alert',
      color: t.target > t.achieved ? '#DC2626' : '#9BA3AE',
      value: t.target > t.achieved ? money(t.target - t.achieved) : 'تحقّق',
      numeric: true,
    },
  ];
}
